// SPDX-License-Identifier: AGPL-3.0-or-later

package kairos

import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.serialization.Serializable

@Serializable data class KairosKpiValuePoint(val at: Instant, val value: Double)

@Serializable
data class KairosKpiEvaluation(
    val kpiId: Kairos.KpiId,
    val currentValue: Double? = null,
    val currentValueAt: Instant? = null,
    val recentPoints: List<KairosKpiValuePoint> = emptyList(),
    val dependencyKpis: List<Kairos.KpiId> = emptyList(),
    val error: String? = null,
)

class KairosKpiFormulaParseException(message: String) : IllegalArgumentException(message)

fun Kairos.KpiExpr.referencedKpis(): Set<Kairos.KpiId> =
    when (this) {
        is Kairos.KpiExpr.Literal -> emptySet()
        is Kairos.KpiExpr.Add -> left.referencedKpis() + right.referencedKpis()
        is Kairos.KpiExpr.Subtract -> left.referencedKpis() + right.referencedKpis()
        is Kairos.KpiExpr.Multiply -> left.referencedKpis() + right.referencedKpis()
        is Kairos.KpiExpr.Divide -> left.referencedKpis() + right.referencedKpis()
        is Kairos.KpiExpr.Negate -> expr.referencedKpis()
        is Kairos.KpiExpr.Reference -> setOf(kpiId)
        is Kairos.KpiExpr.AggregateSeries -> expr.referencedKpis()
        is Kairos.KpiExpr.TicketQuery -> emptySet()
    }

fun formatKairosKpiFormula(
    expr: Kairos.KpiExpr,
    describeKpi: (Kairos.KpiId) -> String = { id -> id.value },
): String = formatKpiExpr(expr, describeKpi, parentPrecedence = 0)

fun parseKairosKpiFormula(text: String, resolveKpi: (String) -> Kairos.KpiId?): Kairos.KpiExpr =
    KairosKpiFormulaParser(text, resolveKpi).parse()

fun buildKairosKpiEvaluations(
    workspace: KairosWorkspaceSnapshot,
    now: Instant = Clock.System.now(),
): List<KairosKpiEvaluation> = KairosKpiEvaluationEngine(workspace, now).build()

private class KairosKpiEvaluationEngine(
    private val workspace: KairosWorkspaceSnapshot,
    private val now: Instant,
) {
    private val kpisById = workspace.kpis.associateBy { kpi -> kpi.id }
    private val measurementsByKpi =
        workspace.measurements
            .groupBy { measurement -> measurement.kpiId }
            .mapValues { (_, measurements) ->
                measurements.sortedBy { measurement -> measurement.recordedAt }
            }
    private val historyCache = mutableMapOf<Kairos.KpiId, List<KairosKpiValuePoint>>()
    private val historyErrors = mutableMapOf<Kairos.KpiId, String?>()

    fun build(): List<KairosKpiEvaluation> =
        workspace.kpis.map { kpi ->
            val history = historyForKpi(kpi.id)
            val currentPoint =
                history.lastOrNull { point -> point.at <= now } ?: history.lastOrNull()
            KairosKpiEvaluation(
                kpiId = kpi.id,
                currentValue = currentPoint?.value,
                currentValueAt = currentPoint?.at,
                recentPoints = history.takeLast(12),
                dependencyKpis =
                    when (val origin = kpi.origin) {
                        is Kairos.KpiOrigin.Measured -> emptyList()
                        is Kairos.KpiOrigin.Derived ->
                            origin.formula.referencedKpis().sortedBy { id -> id.value }
                    },
                error = historyErrors[kpi.id],
            )
        }

    private fun historyForKpi(kpiId: Kairos.KpiId): List<KairosKpiValuePoint> {
        historyCache[kpiId]?.let {
            return it
        }
        val kpi = kpisById[kpiId]
        if (kpi == null) {
            historyErrors[kpiId] = "Unknown KPI ${kpiId.value}"
            return emptyList()
        }
        val history =
            when (val origin = kpi.origin) {
                is Kairos.KpiOrigin.Measured ->
                    measurementsByKpi[kpiId].orEmpty().map { measurement ->
                        KairosKpiValuePoint(at = measurement.recordedAt, value = measurement.value)
                    }
                is Kairos.KpiOrigin.Derived -> derivedHistory(kpi, origin.formula)
            }
        historyCache[kpiId] = history
        return history
    }

    private fun derivedHistory(
        kpi: Kairos.Kpi,
        formula: Kairos.KpiExpr,
    ): List<KairosKpiValuePoint> {
        val dependencyTimes =
            formula
                .referencedKpis()
                .flatMap { dependencyId -> historyForKpi(dependencyId).map { point -> point.at } }
                .distinct()
                .sorted()
                .takeLast(24)
        val anchorTimes =
            when {
                dependencyTimes.isEmpty() -> listOf(now)
                dependencyTimes.last() == now -> dependencyTimes
                else -> dependencyTimes + now
            }
        val points =
            anchorTimes.mapNotNull { asOf ->
                when (val outcome = evaluateExpr(formula, asOf)) {
                    is KpiEvalOutcome.Success -> {
                        val value = outcome.value
                        if (!value.isFinite()) {
                            historyErrors[kpi.id] = "Expression produced a non-finite value."
                            null
                        } else {
                            KairosKpiValuePoint(at = asOf, value = value)
                        }
                    }
                    is KpiEvalOutcome.Missing -> null
                    is KpiEvalOutcome.Error -> {
                        historyErrors[kpi.id] = outcome.message
                        null
                    }
                }
            }
        if (points.isEmpty()) {
            if (historyErrors[kpi.id] == null) {
                historyErrors[kpi.id] = "No evaluated value available."
            }
        }
        return points
    }

    private fun evaluateExpr(expr: Kairos.KpiExpr, asOf: Instant): KpiEvalOutcome =
        when (expr) {
            is Kairos.KpiExpr.Literal -> KpiEvalOutcome.Success(expr.value)
            is Kairos.KpiExpr.Add ->
                combineNumeric(expr.left, expr.right, asOf) { left, right -> left + right }
            is Kairos.KpiExpr.Subtract ->
                combineNumeric(expr.left, expr.right, asOf) { left, right -> left - right }
            is Kairos.KpiExpr.Multiply ->
                combineNumeric(expr.left, expr.right, asOf) { left, right -> left * right }
            is Kairos.KpiExpr.Divide ->
                combineNumeric(expr.left, expr.right, asOf) { left, right ->
                    if (right == 0.0) {
                        null
                    } else {
                        left / right
                    }
                }
            is Kairos.KpiExpr.Negate ->
                when (val inner = evaluateExpr(expr.expr, asOf)) {
                    is KpiEvalOutcome.Success -> KpiEvalOutcome.Success(-inner.value)
                    is KpiEvalOutcome.Missing -> inner
                    is KpiEvalOutcome.Error -> inner
                }
            is Kairos.KpiExpr.Reference -> referenceValue(expr.kpiId, expr.evaluationStrategy, asOf)
            is Kairos.KpiExpr.AggregateSeries ->
                aggregateSeries(expr.expr, expr.aggregationStrategy, asOf)
            is Kairos.KpiExpr.TicketQuery -> ticketQueryValue(expr, asOf)
        }

    private fun combineNumeric(
        left: Kairos.KpiExpr,
        right: Kairos.KpiExpr,
        asOf: Instant,
        op: (Double, Double) -> Double?,
    ): KpiEvalOutcome {
        val leftValue =
            when (val outcome = evaluateExpr(left, asOf)) {
                is KpiEvalOutcome.Success -> outcome.value
                is KpiEvalOutcome.Missing -> return outcome
                is KpiEvalOutcome.Error -> return outcome
            }
        val rightValue =
            when (val outcome = evaluateExpr(right, asOf)) {
                is KpiEvalOutcome.Success -> outcome.value
                is KpiEvalOutcome.Missing -> return outcome
                is KpiEvalOutcome.Error -> return outcome
            }
        val result = op(leftValue, rightValue)
        return if (result == null) {
            KpiEvalOutcome.Error("Expression could not be evaluated.")
        } else {
            KpiEvalOutcome.Success(result)
        }
    }

    private fun referenceValue(
        kpiId: Kairos.KpiId,
        strategy: Kairos.KpiExpr.EvaluationStrategy,
        asOf: Instant,
    ): KpiEvalOutcome {
        val history = historyForKpi(kpiId)
        return valueFromHistory(history, strategy, asOf)
    }

    private fun valueFromHistory(
        history: List<KairosKpiValuePoint>,
        strategy: Kairos.KpiExpr.EvaluationStrategy,
        asOf: Instant,
    ): KpiEvalOutcome =
        when (strategy) {
            Kairos.KpiExpr.EvaluationStrategy.Fail ->
                history
                    .lastOrNull { point -> point.at <= asOf }
                    ?.let { point -> KpiEvalOutcome.Success(point.value) } ?: KpiEvalOutcome.Missing
            is Kairos.KpiExpr.EvaluationStrategy.Default ->
                history
                    .lastOrNull { point -> point.at <= asOf }
                    ?.let { point -> KpiEvalOutcome.Success(point.value) }
                    ?: KpiEvalOutcome.Success(strategy.value)
            is Kairos.KpiExpr.EvaluationStrategy.LOCF -> {
                val current = history.lastOrNull { point -> point.at <= asOf }
                if (current == null) {
                    fallbackValue(history, strategy.fallback, asOf)
                } else {
                    val ageMs = asOf.toEpochMilliseconds() - current.at.toEpochMilliseconds()
                    if (ageMs <= strategy.maxDuration.toApproxMillis()) {
                        KpiEvalOutcome.Success(current.value)
                    } else {
                        fallbackValue(history, strategy.fallback, asOf)
                    }
                }
            }
            is Kairos.KpiExpr.EvaluationStrategy.Extrapolate ->
                extrapolateValue(history, strategy, asOf)
        }

    private fun extrapolateValue(
        history: List<KairosKpiValuePoint>,
        strategy: Kairos.KpiExpr.EvaluationStrategy.Extrapolate,
        asOf: Instant,
    ): KpiEvalOutcome {
        val currentIndex = history.indexOfLast { point -> point.at <= asOf }
        if (currentIndex == -1) {
            return fallbackValue(history, strategy.fallback, asOf)
        }

        val current = history[currentIndex]
        val ageMs = asOf.toEpochMilliseconds() - current.at.toEpochMilliseconds()
        if (ageMs <= 0L) {
            return KpiEvalOutcome.Success(current.value)
        }
        if (ageMs > strategy.duration.toApproxMillis()) {
            return fallbackValue(history, strategy.fallback, asOf)
        }
        if (currentIndex == 0) {
            return fallbackValue(history, strategy.fallback, asOf)
        }

        val previous = history[currentIndex - 1]
        val intervalMs = current.at.toEpochMilliseconds() - previous.at.toEpochMilliseconds()
        if (intervalMs <= 0L) {
            return fallbackValue(history, strategy.fallback, asOf)
        }

        val slope = (current.value - previous.value) / intervalMs.toDouble()
        return KpiEvalOutcome.Success(current.value + (slope * ageMs.toDouble()))
    }

    private fun fallbackValue(
        history: List<KairosKpiValuePoint>,
        strategy: Kairos.KpiExpr.EvaluationStrategy,
        asOf: Instant,
    ): KpiEvalOutcome = valueFromHistory(history, strategy, asOf)

    private fun aggregateSeries(
        expr: Kairos.KpiExpr,
        strategy: Kairos.KpiExpr.SeriesAggregationStrategy,
        asOf: Instant,
    ): KpiEvalOutcome {
        val sourcePoints = historyForExpr(expr, asOf)
        if (sourcePoints.isEmpty()) {
            return KpiEvalOutcome.Missing
        }
        val windowStart = asOf.toEpochMilliseconds() - strategy.windowDuration().toApproxMillis()
        val windowPoints =
            sourcePoints.filter { point ->
                val pointMs = point.at.toEpochMilliseconds()
                pointMs in windowStart..asOf.toEpochMilliseconds()
            }
        if (windowPoints.isEmpty()) {
            return KpiEvalOutcome.Missing
        }
        val value =
            when (strategy) {
                is Kairos.KpiExpr.SeriesAggregationStrategy.Sum -> windowPoints.sumOf { it.value }
                is Kairos.KpiExpr.SeriesAggregationStrategy.SMA ->
                    windowPoints.map { it.value }.average()
                is Kairos.KpiExpr.SeriesAggregationStrategy.EMA -> {
                    val values =
                        windowPoints.sortedBy { point -> point.at }.map { point -> point.value }
                    val alpha = 2.0 / (values.size.toDouble() + 1.0)
                    values.drop(1).fold(values.first()) { acc, pointValue ->
                        (pointValue * alpha) + (acc * (1.0 - alpha))
                    }
                }
            }
        return if (value.isFinite()) {
            KpiEvalOutcome.Success(value)
        } else {
            KpiEvalOutcome.Error("Series aggregation produced a non-finite value.")
        }
    }

    private fun historyForExpr(expr: Kairos.KpiExpr, asOf: Instant): List<KairosKpiValuePoint> =
        when (expr) {
            is Kairos.KpiExpr.Literal -> listOf(KairosKpiValuePoint(at = asOf, value = expr.value))
            is Kairos.KpiExpr.Reference -> historyForKpi(expr.kpiId)
            is Kairos.KpiExpr.Negate ->
                historyForExpr(expr.expr, asOf).map { point ->
                    KairosKpiValuePoint(at = point.at, value = -point.value)
                }
            is Kairos.KpiExpr.Add,
            is Kairos.KpiExpr.Subtract,
            is Kairos.KpiExpr.Multiply,
            is Kairos.KpiExpr.Divide -> {
                val timestamps =
                    when (expr) {
                        is Kairos.KpiExpr.Add ->
                            historyForExpr(expr.left, asOf).map { it.at } +
                                historyForExpr(expr.right, asOf).map { it.at }
                        is Kairos.KpiExpr.Subtract ->
                            historyForExpr(expr.left, asOf).map { it.at } +
                                historyForExpr(expr.right, asOf).map { it.at }
                        is Kairos.KpiExpr.Multiply ->
                            historyForExpr(expr.left, asOf).map { it.at } +
                                historyForExpr(expr.right, asOf).map { it.at }
                        is Kairos.KpiExpr.Divide ->
                            historyForExpr(expr.left, asOf).map { it.at } +
                                historyForExpr(expr.right, asOf).map { it.at }
                        else -> emptyList()
                    }
                timestamps.distinct().sorted().mapNotNull { pointAt ->
                    when (val outcome = evaluateExpr(expr, pointAt)) {
                        is KpiEvalOutcome.Success ->
                            KairosKpiValuePoint(at = pointAt, value = outcome.value)
                        is KpiEvalOutcome.Missing -> null
                        is KpiEvalOutcome.Error -> null
                    }
                }
            }
            is Kairos.KpiExpr.AggregateSeries -> {
                val innerPoints = historyForExpr(expr.expr, asOf)
                val timestamps = (innerPoints.map { point -> point.at } + asOf).distinct().sorted()
                timestamps.mapNotNull { pointAt ->
                    when (
                        val outcome = aggregateSeries(expr.expr, expr.aggregationStrategy, pointAt)
                    ) {
                        is KpiEvalOutcome.Success ->
                            KairosKpiValuePoint(at = pointAt, value = outcome.value)
                        is KpiEvalOutcome.Missing -> null
                        is KpiEvalOutcome.Error -> null
                    }
                }
            }
            is Kairos.KpiExpr.TicketQuery -> {
                when (val outcome = ticketQueryValue(expr, asOf)) {
                    is KpiEvalOutcome.Success ->
                        listOf(KairosKpiValuePoint(at = asOf, value = outcome.value))
                    is KpiEvalOutcome.Missing,
                    is KpiEvalOutcome.Error -> emptyList()
                }
            }
        }

    private fun ticketQueryValue(expr: Kairos.KpiExpr.TicketQuery, asOf: Instant): KpiEvalOutcome {
        val matchingTickets =
            workspace.tickets.filter { ticket -> ticketMatchesQuery(ticket, expr.filter, asOf) }
        val values =
            matchingTickets.mapNotNull { ticket -> ticketFieldValue(ticket, expr.query, asOf) }
        val value =
            when (expr.aggregationStrategy) {
                Kairos.KpiExpr.SetAggregationStrategy.Count -> matchingTickets.size.toDouble()
                Kairos.KpiExpr.SetAggregationStrategy.Sum -> values.sum()
                Kairos.KpiExpr.SetAggregationStrategy.Average ->
                    values.takeIf { valuesForAverage -> valuesForAverage.isNotEmpty() }?.average()
                Kairos.KpiExpr.SetAggregationStrategy.Min -> values.minOrNull()
                Kairos.KpiExpr.SetAggregationStrategy.Max -> values.maxOrNull()
            }
        return if (value == null) KpiEvalOutcome.Missing else KpiEvalOutcome.Success(value)
    }

    private fun ticketMatchesQuery(
        ticket: Kairos.Ticket,
        query: BoolExpr<Kairos.TicketQ>,
        asOf: Instant,
    ): Boolean =
        when (query) {
            is BoolExpr.And -> query.exprs.all { expr -> ticketMatchesQuery(ticket, expr, asOf) }
            is BoolExpr.Or -> query.exprs.any { expr -> ticketMatchesQuery(ticket, expr, asOf) }
            is BoolExpr.Not -> !ticketMatchesQuery(ticket, query.expr, asOf)
            is BoolExpr.Primitive -> ticketMatches(ticket, query.value, asOf)
        }

    private fun ticketMatches(
        ticket: Kairos.Ticket,
        query: Kairos.TicketQ,
        asOf: Instant,
    ): Boolean =
        when (query) {
            Kairos.TicketQ.IsOpen -> ticket.completedAt == null
            Kairos.TicketQ.IsClosed -> ticket.completedAt != null
            Kairos.TicketQ.IsOverdue ->
                ticket.deadline?.let { deadline -> deadline.toInstant() < asOf } ?: false
            is Kairos.TicketQ.InWorkspace ->
                ticket.projectId in workspace.projects.map { it.id }.toSet() &&
                    workspace.workspace.id == query.workspaceId
            is Kairos.TicketQ.WithPriority -> ticket.priority == query.priority
            is Kairos.TicketQ.WithUrgency -> ticket.urgency == query.urgency
            is Kairos.TicketQ.WithStage -> ticket.stage == query.stage
            is Kairos.TicketQ.AssignedTo -> ticket.assigneeUserId == query.userId
        }

    private fun ticketFieldValue(
        ticket: Kairos.Ticket,
        field: Kairos.TicketField,
        asOf: Instant,
    ): Double? =
        when (field) {
            Kairos.TicketField.TimeSinceCreation ->
                ((asOf.toEpochMilliseconds() - ticket.createdAt.toEpochMilliseconds()) /
                        3_600_000.0)
                    .takeIf { value -> value.isFinite() }
            Kairos.TicketField.TimeUntilDeadline ->
                ticket.deadline?.let { deadline ->
                    ((deadline.toInstant().toEpochMilliseconds() - asOf.toEpochMilliseconds()) /
                        3_600_000.0)
                }
        }
}

private sealed interface KpiEvalOutcome {
    data class Success(val value: Double) : KpiEvalOutcome

    data object Missing : KpiEvalOutcome

    data class Error(val message: String) : KpiEvalOutcome
}

private fun formatKpiExpr(
    expr: Kairos.KpiExpr,
    describeKpi: (Kairos.KpiId) -> String,
    parentPrecedence: Int,
): String {
    val precedence = expr.precedence()
    val rendered =
        when (expr) {
            is Kairos.KpiExpr.Literal -> expr.value.toFormulaNumber()
            is Kairos.KpiExpr.Add ->
                "${formatKpiExpr(expr.left, describeKpi, precedence)} + ${formatKpiExpr(expr.right, describeKpi, precedence)}"
            is Kairos.KpiExpr.Subtract ->
                "${formatKpiExpr(expr.left, describeKpi, precedence)} - ${formatKpiExpr(expr.right, describeKpi, precedence + 1)}"
            is Kairos.KpiExpr.Multiply ->
                "${formatKpiExpr(expr.left, describeKpi, precedence)} * ${formatKpiExpr(expr.right, describeKpi, precedence)}"
            is Kairos.KpiExpr.Divide ->
                "${formatKpiExpr(expr.left, describeKpi, precedence)} / ${formatKpiExpr(expr.right, describeKpi, precedence + 1)}"
            is Kairos.KpiExpr.Negate -> "-${formatKpiExpr(expr.expr, describeKpi, precedence)}"
            is Kairos.KpiExpr.Reference -> {
                val label = describeKpi(expr.kpiId)
                "kpi(${label.toFormulaReferenceLiteral()})"
            }
            is Kairos.KpiExpr.AggregateSeries ->
                "series(${formatKpiExpr(expr.expr, describeKpi, 0)}, ${expr.aggregationStrategy.toFormulaText()})"
            is Kairos.KpiExpr.TicketQuery ->
                "ticket(${expr.query.toFormulaText()}, ${expr.aggregationStrategy.toFormulaText()})"
        }
    return if (precedence < parentPrecedence) "($rendered)" else rendered
}

private fun Kairos.KpiExpr.precedence(): Int =
    when (this) {
        is Kairos.KpiExpr.Add,
        is Kairos.KpiExpr.Subtract -> 1
        is Kairos.KpiExpr.Multiply,
        is Kairos.KpiExpr.Divide -> 2
        is Kairos.KpiExpr.Negate -> 3
        else -> 4
    }

private fun Double.toFormulaNumber(): String =
    if (this % 1.0 == 0.0) {
        toLong().toString()
    } else {
        toString()
    }

private fun String.toFormulaReferenceLiteral(): String {
    val simple = all { char -> char.isLetterOrDigit() || char == '-' || char == '_' || char == '.' }
    return if (simple) this else "\"${replace("\"", "\\\"")}\""
}

private fun Kairos.KpiExpr.SeriesAggregationStrategy.toFormulaText(): String =
    when (this) {
        is Kairos.KpiExpr.SeriesAggregationStrategy.Sum -> "sum ${duration.toFormulaText()}"
        is Kairos.KpiExpr.SeriesAggregationStrategy.SMA ->
            "sma ${duration.toFormulaText()}${granularity?.let { unit -> " / ${unit.shortFormulaLabel()}" }.orEmpty()}"
        is Kairos.KpiExpr.SeriesAggregationStrategy.EMA ->
            "ema ${duration.toFormulaText()}${granularity?.let { unit -> " / ${unit.shortFormulaLabel()}" }.orEmpty()}"
    }

private fun Kairos.KpiExpr.SetAggregationStrategy.toFormulaText(): String =
    when (this) {
        Kairos.KpiExpr.SetAggregationStrategy.Sum -> "sum"
        Kairos.KpiExpr.SetAggregationStrategy.Average -> "avg"
        Kairos.KpiExpr.SetAggregationStrategy.Min -> "min"
        Kairos.KpiExpr.SetAggregationStrategy.Max -> "max"
        Kairos.KpiExpr.SetAggregationStrategy.Count -> "count"
    }

private fun Kairos.TicketField.toFormulaText(): String =
    when (this) {
        Kairos.TicketField.TimeSinceCreation -> "ticket.timeSinceCreation"
        Kairos.TicketField.TimeUntilDeadline -> "ticket.timeUntilDeadline"
    }

private fun TimeSpan.toApproxMillis(): Long =
    spans.fold(0L) { total, span ->
        total +
            when (span.unit) {
                TimeUnit.Seconds -> span.value * 1_000L
                TimeUnit.Minutes -> span.value * 60_000L
                TimeUnit.Hours -> span.value * 3_600_000L
                TimeUnit.Days -> span.value * 86_400_000L
                TimeUnit.Weeks -> span.value * 7L * 86_400_000L
                TimeUnit.Months -> span.value * 30L * 86_400_000L
                TimeUnit.Years -> span.value * 365L * 86_400_000L
            }
    }

private fun Kairos.KpiExpr.SeriesAggregationStrategy.windowDuration(): TimeSpan =
    when (this) {
        is Kairos.KpiExpr.SeriesAggregationStrategy.Sum -> duration
        is Kairos.KpiExpr.SeriesAggregationStrategy.SMA -> duration
        is Kairos.KpiExpr.SeriesAggregationStrategy.EMA -> duration
    }

private fun TimeSpan.toFormulaText(): String =
    spans.joinToString(" ") { span -> "${span.value}${span.unit.shortFormulaLabel()}" }

private fun TimeUnit.shortFormulaLabel(): String =
    when (this) {
        TimeUnit.Seconds -> "s"
        TimeUnit.Minutes -> "m"
        TimeUnit.Hours -> "h"
        TimeUnit.Days -> "d"
        TimeUnit.Weeks -> "w"
        TimeUnit.Months -> "mo"
        TimeUnit.Years -> "y"
    }

private class KairosKpiFormulaParser(
    source: String,
    private val resolveKpi: (String) -> Kairos.KpiId?,
) {
    private val text = source.trim()
    private var index = 0

    fun parse(): Kairos.KpiExpr {
        if (text.isBlank()) {
            throw KairosKpiFormulaParseException("Formula must not be blank.")
        }
        val expr = parseExpression()
        skipWhitespace()
        if (index != text.length) {
            throw KairosKpiFormulaParseException("Unexpected token at position ${index + 1}.")
        }
        return expr
    }

    private fun parseExpression(): Kairos.KpiExpr {
        var expr = parseTerm()
        while (true) {
            skipWhitespace()
            expr =
                when {
                    consume('+') -> Kairos.KpiExpr.Add(expr, parseTerm())
                    consume('-') -> Kairos.KpiExpr.Subtract(expr, parseTerm())
                    else -> return expr
                }
        }
    }

    private fun parseTerm(): Kairos.KpiExpr {
        var expr = parseFactor()
        while (true) {
            skipWhitespace()
            expr =
                when {
                    consume('*') -> Kairos.KpiExpr.Multiply(expr, parseFactor())
                    consume('/') -> Kairos.KpiExpr.Divide(expr, parseFactor())
                    else -> return expr
                }
        }
    }

    private fun parseFactor(): Kairos.KpiExpr {
        skipWhitespace()
        return when {
            consume('-') -> Kairos.KpiExpr.Negate(parseFactor())
            consume('(') -> {
                val inner = parseExpression()
                if (!consume(')')) {
                    throw KairosKpiFormulaParseException("Missing closing ')' in KPI formula.")
                }
                inner
            }
            startsWith("kpi(") -> parseReference()
            peek()?.isDigit() == true || peek() == '.' -> parseLiteral()
            else ->
                throw KairosKpiFormulaParseException(
                    "Expected number, kpi(...), or '(' at position ${index + 1}."
                )
        }
    }

    private fun parseReference(): Kairos.KpiExpr.Reference {
        if (!startsWith("kpi(")) {
            throw KairosKpiFormulaParseException("Expected kpi(...) at position ${index + 1}.")
        }
        index += 4
        skipWhitespace()
        val rawReference =
            when (val quote = peek()) {
                '"' -> parseQuotedString(quote)
                '\'' -> parseQuotedString(quote)
                else -> parseIdentifierUntil(')')
            }
        skipWhitespace()
        if (!consume(')')) {
            throw KairosKpiFormulaParseException("Missing closing ')' for KPI reference.")
        }
        val kpiId =
            resolveKpi(rawReference)
                ?: throw KairosKpiFormulaParseException("Unknown KPI reference '$rawReference'.")
        return Kairos.KpiExpr.Reference(
            kpiId = kpiId,
            evaluationStrategy = Kairos.KpiExpr.EvaluationStrategy.Default(0.0),
        )
    }

    private fun parseLiteral(): Kairos.KpiExpr.Literal {
        val start = index
        var seenDot = false
        while (index < text.length) {
            val char = text[index]
            when {
                char.isDigit() -> index += 1
                char == '.' && !seenDot -> {
                    seenDot = true
                    index += 1
                }
                else -> break
            }
        }
        val literal = text.substring(start, index)
        val value =
            literal.toDoubleOrNull()
                ?: throw KairosKpiFormulaParseException("Invalid numeric literal '$literal'.")
        return Kairos.KpiExpr.Literal(value)
    }

    private fun parseQuotedString(quote: Char): String {
        if (!consume(quote)) {
            throw KairosKpiFormulaParseException("Expected quote at position ${index + 1}.")
        }
        val builder = StringBuilder()
        while (index < text.length) {
            val char = text[index]
            index += 1
            when {
                char == quote -> return builder.toString()
                char == '\\' && index < text.length -> {
                    builder.append(text[index])
                    index += 1
                }
                else -> builder.append(char)
            }
        }
        throw KairosKpiFormulaParseException("Unterminated quoted KPI reference.")
    }

    private fun parseIdentifierUntil(terminal: Char): String {
        val start = index
        while (index < text.length && text[index] != terminal) {
            index += 1
        }
        return text.substring(start, index).trim().takeIf { value -> value.isNotBlank() }
            ?: throw KairosKpiFormulaParseException("KPI reference must not be blank.")
    }

    private fun startsWith(value: String): Boolean =
        text.regionMatches(index, value, 0, value.length)

    private fun consume(expected: Char): Boolean =
        if (peek() == expected) {
            index += 1
            true
        } else {
            false
        }

    private fun peek(): Char? = text.getOrNull(index)

    private fun skipWhitespace() {
        while (index < text.length && text[index].isWhitespace()) {
            index += 1
        }
    }
}
